import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Star,
  Search,
  EyeOff,
  Eye,
  Trash2,
  ExternalLink,
  MessageSquare,
  Loader2,
  Building2,
  ChevronRight,
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { getAsset } from '@/client/utils/assets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useListingQuery } from '@/client/api/listing-management/listing.query';
import {
  useFeedbackListQuery,
  useHideFeedbackMutation,
  useRestoreFeedbackMutation,
  useDeleteFeedbackMutation,
  AdminFeedback,
} from '@/client/api/listing-management/feedback.query';

const truthy = (v: unknown) => v === 1 || v === true || v === '1';
const fbId = (r: AdminFeedback) => Number(r.fbid ?? r.feedback_id ?? r.id);

// The listing endpoint nests each row under `Entity`; guard every access.
const entEid = (ent: any): number | undefined => ent?.Entity?.eid ?? ent?.eid;
const entName = (ent: any): string =>
  ent?.Entity?.name_basic ||
  ent?.Entity?.name ||
  ent?.name_basic ||
  ent?.name ||
  `Listing #${entEid(ent) ?? '—'}`;
const entType = (ent: any): string =>
  ent?.EntityType?.name_friendly || ent?.EntityType?.name_system || ent?.type_name || '';

const PAGE_SIZE = 25;

const ReviewManagementSystem = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Reviews & Ratings' },
  ]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Mode, the selected listing, and both panes' search + pagination all persist
  // in the URL so a reload (or a shared link) restores the full view.
  const mode: 'listing' | 'search' = searchParams.get('mode') === 'search' ? 'search' : 'listing';

  // ── Listings browser (left pane) — URL params namespaced 'l' ──
  const left = useListFilters({ initialFilters: {}, initialLimit: 10, namespace: 'l' });
  const listingSearch = left.searchTerm;
  const debListingSearch = left.debouncedSearchTerm.trim();
  const listingPage = left.page;

  const {
    data: listingData,
    isLoading: listingsLoading,
    isFetching: listingsFetching,
  } = useListingQuery(listingPage, {
    per_page: 10,
    name: debListingSearch,
    // NOTE: `/api/admin/entities` has no `visibility` filter — passing
    // `visibility=all` makes it return zero rows, so it is intentionally omitted
    // and the full default listing set comes back. Reviews on any listing that
    // the default set hides are still reachable via "Search all reviews" mode.
  });
  const listings: any[] = listingData?.entities ?? [];
  const listingTotalPages = listingData?.total_pages ?? 1;

  // ── Reviews (right pane / search mode) — URL params namespaced 'r' ──
  const right = useListFilters({ initialFilters: {}, initialLimit: PAGE_SIZE, namespace: 'r' });
  const reviewSearch = right.searchTerm;
  const debReviewSearch = right.debouncedSearchTerm.trim();
  const page = right.page;
  const [pendingDelete, setPendingDelete] = useState<AdminFeedback | null>(null);

  const eidParam = searchParams.get('eid');
  const selected = eidParam
    ? { eid: Number(eidParam), name: searchParams.get('ename') || `Listing #${eidParam}` }
    : null;

  const patchParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );

  // react-router resolves `setSearchParams((prev) => …)` against the *current*
  // location, not an accumulating value, so a second call in the same handler
  // (e.g. right.handlePageChange) clobbers the first and drops the params we just
  // set. Each handler below therefore does a single write and resets the reviews
  // page (`r_page`) inline instead of calling handlePageChange separately.
  const setMode = (m: 'listing' | 'search') => {
    patchParams((p) => {
      if (m === 'listing') p.delete('mode');
      else p.set('mode', m);
      p.delete('r_page');
    });
  };

  const selectListing = (eid: number, name: string) => {
    patchParams((p) => {
      p.set('eid', String(eid));
      p.set('ename', name);
      p.delete('r_page');
    });
  };

  const entityId = mode === 'listing' ? selected?.eid : undefined;
  const search = mode === 'search' ? debReviewSearch : undefined;

  const { data, isLoading, isError, isFetching } = useFeedbackListQuery({
    entity_id: entityId,
    search,
    page,
    page_size: PAGE_SIZE,
  });

  const hideMut = useHideFeedbackMutation();
  const restoreMut = useRestoreFeedbackMutation();
  const deleteMut = useDeleteFeedbackMutation();

  const rows = data?.feedback ?? [];
  const hasQuery = !!entityId || !!search;

  const toggleHidden = async (r: AdminFeedback) => {
    const id = fbId(r);
    try {
      if (truthy(r.deleted ?? r.hidden)) {
        await restoreMut.mutateAsync(id);
        toast.success('Review restored');
      } else {
        await hideMut.mutateAsync(id);
        toast.success('Review hidden');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the review.');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(fbId(pendingDelete));
      toast.success('Review deleted');
      setPendingDelete(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete the review.');
    }
  };

  const renderReviewRows = () => {
    if (hasQuery && isLoading) {
      return Array.from({ length: 5 }).map((_, i) => (
        <tr key={i}>
          {Array.from({ length: 6 }).map((__, j) => (
            <td key={j} className="px-6 py-4">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ));
    }
    if (hasQuery && !isLoading && isError) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-sm text-red-600">
            Could not load reviews.
          </td>
        </tr>
      );
    }
    if (hasQuery && !isLoading && !isError && rows.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
            No reviews found.
          </td>
        </tr>
      );
    }
    if (!hasQuery) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-16 text-center">
            <MessageSquare className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-500">
              {mode === 'listing'
                ? 'Select a listing on the left to view its reviews.'
                : 'Type a search term to find reviews across all listings.'}
            </p>
          </td>
        </tr>
      );
    }
    return rows.map((r) => {
      const hidden = truthy(r.deleted ?? r.hidden);
      return (
        <tr key={fbId(r)} className={`hover:bg-gray-50 ${hidden ? 'opacity-60' : ''}`}>
          <td className="max-w-sm px-6 py-4">
            {r.title && <div className="text-sm font-medium text-gray-900">{r.title}</div>}
            <div className="line-clamp-2 text-sm text-gray-600">{r.content ?? '—'}</div>
          </td>
          <td className="whitespace-nowrap px-6 py-4">
            {r.eid ? (
              <button
                onClick={() => navigate(`/admin/listings/${r.eid}`)}
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
              >
                #{r.eid}
                <ExternalLink className="h-3 w-3" />
              </button>
            ) : (
              '—'
            )}
          </td>
          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
            {r.uid_actor ? `uid ${r.uid_actor}` : '—'}
          </td>
          <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-500">
            {r.date_added ? new Date(r.date_added).toLocaleDateString() : '—'}
          </td>
          <td className="whitespace-nowrap px-6 py-4">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                hidden ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-800'
              }`}
            >
              {hidden ? 'Hidden' : 'Visible'}
            </span>
          </td>
          <td className="whitespace-nowrap px-6 py-4 text-right">
            <div className="flex items-center justify-end gap-1.5">
              <button
                onClick={() => toggleHidden(r)}
                disabled={hideMut.isPending || restoreMut.isPending}
                className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
                title={hidden ? 'Restore' : 'Hide'}
              >
                {hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setPendingDelete(r)}
                className="rounded-md p-1.5 text-rose-600 hover:bg-rose-50"
                title="Delete permanently"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Star className="h-7 w-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reviews &amp; Ratings</h1>
            <p className="text-sm text-gray-500">
              Browse listings and moderate their reviews — hide, restore, or permanently delete.
            </p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          {(['listing', 'search'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                mode === m ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m === 'listing' ? 'Browse by listing' : 'Search all reviews'}
            </button>
          ))}
        </div>

        <div className={`flex gap-6 ${mode === 'search' ? 'flex-col' : 'flex-col lg:flex-row'}`}>
          {/* Listings browser */}
          {mode === 'listing' && (
            <aside className="w-full flex-shrink-0 lg:w-80">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 p-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      className="pl-9"
                      placeholder="Search listings…"
                      value={listingSearch}
                      onChange={(e) => left.handleSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="max-h-[560px] overflow-y-auto">
                  {listingsLoading ? (
                    <div className="space-y-2 p-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : listings.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-gray-400">No listings found.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {listings.map((ent) => {
                        const eid = entEid(ent);
                        if (eid == null) return null;
                        const isSel = selected?.eid === eid;
                        const cover = getAsset(ent?.Assets?.[0]);
                        return (
                          <li key={eid}>
                            <button
                              onClick={() => selectListing(eid, entName(ent))}
                              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                isSel ? 'bg-indigo-50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                {cover ? (
                                  <img src={cover} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                                    <Building2 className="h-5 w-5" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`truncate text-sm font-medium ${isSel ? 'text-indigo-700' : 'text-gray-900'}`}>
                                  {entName(ent)}
                                </p>
                                <p className="truncate text-xs text-gray-400">
                                  #{eid}
                                  {entType(ent) ? ` · ${entType(ent)}` : ''}
                                </p>
                              </div>
                              <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isSel ? 'text-indigo-500' : 'text-gray-300'}`} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {/* Listings pagination */}
                <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2">
                  <span className="text-xs text-gray-400">
                    Page {listingPage} of {listingTotalPages || 1}
                    {listingsFetching && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={listingPage <= 1 || listingsFetching}
                      onClick={() => left.handlePageChange(Math.max(1, listingPage - 1))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={listingPage >= (listingTotalPages || 1) || listingsFetching}
                      onClick={() => left.handlePageChange(listingPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* Reviews */}
          <div className="min-w-0 flex-1">
            {/* Query bar */}
            <div className="mb-3 flex items-center gap-3">
              {mode === 'search' ? (
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    className="pl-9"
                    placeholder="Search review text across all listings…"
                    value={reviewSearch}
                    onChange={(e) => right.handleSearch(e.target.value)}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  {selected ? (
                    <>
                      <span className="font-medium text-gray-900">{selected.name}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                        #{selected.eid}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">No listing selected</span>
                  )}
                </div>
              )}
              {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Review', 'Listing', 'Author', 'Date', 'Status', 'Actions'].map((h) => (
                        <th
                          key={h}
                          className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                            h === 'Actions' ? 'text-right' : 'text-left'
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">{renderReviewRows()}</tbody>
                </table>
              </div>

              {hasQuery && !isLoading && !isError && (
                <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
                  <p className="text-xs text-gray-500">
                    {data?.count ?? rows.length} on page {page}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1 || isFetching}
                      onClick={() => right.handlePageChange(Math.max(1, page - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={rows.length < PAGE_SIZE || isFetching}
                      onClick={() => right.handlePageChange(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This hard-deletes the review — it cannot be undone. To temporarily
              remove it instead, use Hide.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleteMut.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete review'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewManagementSystem;
